/**
 * フォーム回答の集計ユーティリティ。
 * admin-web (まとめタブ・統計タブ) と Cloud Function (公開ビュー生成) の両方で使う。
 *
 * v3.1.4 — Phase 統計機能。
 */
import type { FieldAggregation } from '../types/form-analytics';
import type {
  FieldFormElement,
  FormElement,
  FormFieldType,
  FormSubmission,
} from '../types/form';

const CATEGORICAL_TYPES = new Set<FormFieldType>([
  'select',
  'radio',
  'checkbox',
  'consent',
  'reply_request',
]);
const NUMERIC_TYPES = new Set<FormFieldType>(['number']);
const DATE_TYPES = new Set<FormFieldType>(['date']);
const TEXT_TYPES = new Set<FormFieldType>(['text', 'textarea', 'email', 'tel']);
/**
 * 個人情報につき集計対象から外すフィールドタイプ。
 * email/tel は通常一意で集計の意味も薄く、また公開ビューに値が出るのは危険。
 */
const PRIVATE_TEXT_TYPES = new Set<FormFieldType>(['email', 'tel']);

/** Form.elements から FieldFormElement のみ抽出して順序保持。 */
export function extractFieldElements(
  elements: FormElement[] | undefined,
): FieldFormElement[] {
  if (!elements) return [];
  return elements.filter((e): e is FieldFormElement => e.kind === 'field');
}

/**
 * 1 つの設問について全 submission を集計する。
 *
 * @param field      集計対象の質問
 * @param submissions form_submissions の配列 (filter された後でも OK)
 * @param options.includeText  自由テキストを返り値に含めるか (公開ビューでは false)
 */
export function aggregateField(
  field: FieldFormElement,
  submissions: Array<Pick<FormSubmission, 'values'>>,
  options?: { includeText?: boolean },
): FieldAggregation {
  const includeText = options?.includeText ?? true;
  const total = submissions.length;
  let answered = 0;

  const isCategorical = CATEGORICAL_TYPES.has(field.fieldType);
  const isNumeric = NUMERIC_TYPES.has(field.fieldType);
  const isDate = DATE_TYPES.has(field.fieldType);
  const isText = TEXT_TYPES.has(field.fieldType);

  const optionCounts: Record<string, number> = {};
  // checkbox/select/radio で options が事前定義されていれば、0 件でもキーを並べる
  if (isCategorical && Array.isArray(field.options)) {
    for (const opt of field.options) optionCounts[opt] = 0;
  }
  if (field.fieldType === 'consent') {
    optionCounts['同意'] = 0;
    optionCounts['未同意'] = 0;
  }

  const numericValues: number[] = [];
  const dateValues: string[] = [];
  const textValues: string[] = [];

  for (const sub of submissions) {
    const v = sub.values?.[field.id];
    const isEmpty =
      v === undefined ||
      v === null ||
      (typeof v === 'string' && v.trim() === '') ||
      (Array.isArray(v) && v.length === 0);
    if (isEmpty) continue;
    answered++;

    if (isCategorical) {
      if (field.fieldType === 'consent') {
        const sv = String(Array.isArray(v) ? v[0] ?? '' : v).toLowerCase();
        const consented =
          sv === 'true' ||
          sv === '同意' ||
          sv === 'yes' ||
          sv === '1';
        const key = consented ? '同意' : '未同意';
        optionCounts[key] = (optionCounts[key] ?? 0) + 1;
      } else {
        const list = Array.isArray(v) ? v : [String(v)];
        for (const raw of list) {
          const key = String(raw).trim();
          if (!key) continue;
          optionCounts[key] = (optionCounts[key] ?? 0) + 1;
        }
      }
    } else if (isNumeric) {
      const n = Number(String(v).replace(/[,¥\s]/g, ''));
      if (Number.isFinite(n)) numericValues.push(n);
    } else if (isDate) {
      dateValues.push(String(v));
    } else if (isText) {
      // text/textarea は頻度集計を optionCounts に入れる (姓・名・住所など)。
      // email/tel は個人情報保護のため optionCounts に入れない。
      if (!PRIVATE_TEXT_TYPES.has(field.fieldType)) {
        const key = String(v).trim();
        if (key) {
          optionCounts[key] = (optionCounts[key] ?? 0) + 1;
        }
      }
      if (includeText) textValues.push(String(v));
    } else {
      // 動的フィールド (briefing_schedule_picker 等) は文字列として扱う
      const key = String(v).trim();
      if (key) {
        optionCounts[key] = (optionCounts[key] ?? 0) + 1;
      }
      if (includeText) textValues.push(String(v));
    }
  }

  const result: FieldAggregation = {
    fieldId: field.id,
    label: field.label,
    fieldType: field.fieldType,
    answeredCount: answered,
    totalSubmissions: total,
  };
  if (Object.keys(optionCounts).length > 0) result.optionCounts = optionCounts;
  if (numericValues.length > 0) result.numericValues = numericValues;
  if (dateValues.length > 0) result.dateValues = dateValues;
  if (textValues.length > 0) result.textValues = textValues;
  return result;
}

/** 全 FieldFormElement を集計する。 */
export function aggregateAllFields(
  elements: FormElement[] | undefined,
  submissions: Array<Pick<FormSubmission, 'values'>>,
  options?: { includeText?: boolean },
): FieldAggregation[] {
  return extractFieldElements(elements).map((f) =>
    aggregateField(f, submissions, options),
  );
}
